import type { BookDisplayRect, DisplayMode } from '@/types/book';

const DEFAULT_DISPLAY_RECT: BookDisplayRect = { x: 0, y: 0, width: 0.95, height: 0.85 };
const MOBILE_DEFAULT_DISPLAY_RECT: BookDisplayRect = { x: 0, y: 0, width: 0.93, height: 0.97 };
const SIDE_CONTROL_LAYOUT = {
  buttonWidth: 50,
  insetRatio: 0.022,
  insetPx: 0,
  bookGap: 16
};

export interface ResolveReaderBookLayoutInput {
  canvasWidth: number;
  canvasHeight: number;
  isPC: boolean;
  pageWidth: number;
  pageHeight: number;
  displayMode: DisplayMode;
  displayRect?: BookDisplayRect;
}

export interface ResolvedReaderBookLayout {
  canvasWidth: number;
  canvasHeight: number;
  displayRect: BookDisplayRect;
  pageWidth: number;
  pageHeight: number;
  bookWidth: number;
  bookHeight: number;
}

export interface ResolvedReaderBookTransform {
  canvasWidth: number;
  canvasHeight: number;
  displayRect: BookDisplayRect;
  pageWidth: number;
  pageHeight: number;
  bookWidth: number;
  bookHeight: number;
  scale: number;
}

const resolveReaderBookFit = (input: ResolveReaderBookLayoutInput) => {
  const { canvasWidth, canvasHeight, isPC, pageWidth, pageHeight, displayMode } = input;
  if (canvasWidth <= 0 || canvasHeight <= 0 || pageWidth <= 0 || pageHeight <= 0) return null;

  const displayRect =
    input.displayRect ?? (isPC ? DEFAULT_DISPLAY_RECT : MOBILE_DEFAULT_DISPLAY_RECT);
  const pageCount = displayMode === 'double' ? 2 : 1;
  const displayMaxWidth = canvasWidth * displayRect.width;
  const controlsReservedWidth = isPC
    ? (SIDE_CONTROL_LAYOUT.buttonWidth +
        SIDE_CONTROL_LAYOUT.insetPx +
        canvasWidth * SIDE_CONTROL_LAYOUT.insetRatio) *
      2
    : 0;
  const controlsGap = isPC ? SIDE_CONTROL_LAYOUT.bookGap * 2 : 0;
  const maxBookWidth = Math.min(
    displayMaxWidth,
    Math.max(0, canvasWidth - controlsReservedWidth - controlsGap)
  );
  const maxBookHeight = canvasHeight * displayRect.height;
  const bookWidth = pageWidth * pageCount;
  const scale = Math.min(maxBookWidth / bookWidth, maxBookHeight / pageHeight);
  if (!Number.isFinite(scale) || scale <= 0) return null;

  return {
    canvasWidth,
    canvasHeight,
    displayRect,
    pageCount,
    pageWidth,
    pageHeight,
    bookWidth,
    bookHeight: pageHeight,
    scale
  };
};

export const resolveReaderBookLayout = (
  input: ResolveReaderBookLayoutInput
): ResolvedReaderBookLayout | null => {
  const fit = resolveReaderBookFit(input);
  if (!fit) return null;

  const resolvedPageWidth = Math.max(1, Math.floor(fit.pageWidth * fit.scale));
  const resolvedPageHeight = Math.max(1, Math.floor(fit.pageHeight * fit.scale));

  return {
    canvasWidth: fit.canvasWidth,
    canvasHeight: fit.canvasHeight,
    displayRect: fit.displayRect,
    pageWidth: resolvedPageWidth,
    pageHeight: resolvedPageHeight,
    bookWidth: resolvedPageWidth * fit.pageCount,
    bookHeight: resolvedPageHeight
  };
};

/** 视口变化后基于首次渲染尺寸计算原有的 root scale + position 布局。 */
export const resolveReaderBookTransform = (
  input: ResolveReaderBookLayoutInput
): ResolvedReaderBookTransform | null => {
  const fit = resolveReaderBookFit(input);
  if (!fit) return null;

  return {
    canvasWidth: fit.canvasWidth,
    canvasHeight: fit.canvasHeight,
    displayRect: fit.displayRect,
    pageWidth: fit.pageWidth,
    pageHeight: fit.pageHeight,
    bookWidth: fit.bookWidth,
    bookHeight: fit.bookHeight,
    scale: fit.scale
  };
};
